// ============================================
// 场景数据绑定 Store（阶段4｜G3）：绑定规则的内存态 + CRUD 落库
// device-link 绑定 = 模型 ↔ 设备（deviceId），加载回填 models.deviceId（3D 联动）；
// setDeviceBinding(deviceId, 空) = 增改/删除对应绑定（PropertyPanel 设备联动区调用）；
// flush(sceneId, models) = 场景保存时把内存 models.deviceId 全量替换成绑定列表。
// ============================================

import { create } from 'zustand';
import type { SceneBindingRecord } from '../api/bindings';
import {
  fetchSceneBindings,
  createSceneBinding,
  updateSceneBinding,
  removeSceneBinding,
  replaceSceneBindings,
} from '../api/bindings';

/** 绑定属于哪个视觉类型（device-link = 阶段3/4 模型↔设备联动映射） */
export const DEVICE_LINK_TYPE = 'device-link';

interface SceneBindingState {
  bindings: SceneBindingRecord[];
  loading: boolean;
  /** 加载场景绑定；device-link 回填功能在调用侧（SceneEditor）完成（需要 models 上下文） */
  load: (sceneId: string) => Promise<void>;
  /** 设置/清除 模型↔设备 绑定（PropertyPanel 设备联动）。返回成功与否 */
  setDeviceBinding: (
    sceneId: string,
    modelNodeId: string,
    modelNodeName: string,
    deviceId: string | null,
  ) => Promise<boolean>;
  /** 阶段5｜视觉规则绑定：模型+设备点位 → 视觉规则（visual_type + bindPointKey + ruleConfig）增改删。
   *  已有同 visual_type+model 绑定则更新；rule 为空则删除。返回成功与否 */
  setVisualBinding: (
    sceneId: string,
    modelNodeId: string,
    modelNodeName: string,
    visualType: string,
    data: { deviceId?: string; bindPointKey?: string; ruleConfig?: Record<string, unknown> },
  ) => Promise<boolean>;
  /** 阶段6｜合并写入指定绑定的 ruleConfig（如 device-link 的悬浮标签开关 ruleConfig.label） */
  updateBindingRule: (sceneId: string, bindingId: string, patch: Record<string, unknown>) => Promise<boolean>;
  /** 场景保存 flush：内存 models.deviceId → 绑定全量替换 */
  flushDeviceLinks: (sceneId: string, models: { id: string; name: string; deviceId?: string }[]) => Promise<boolean>;
  clear: () => void;
}

export const useSceneBindingStore = create<SceneBindingState>((set, get) => ({
  bindings: [],
  loading: false,

  load: async (sceneId) => {
    set({ loading: true });
    try {
      const bindings = await fetchSceneBindings(sceneId);
      set({ bindings, loading: false });
    } catch {
      set({ bindings: [], loading: false });
    }
  },

  setDeviceBinding: async (sceneId, modelNodeId, modelNodeName, deviceId) => {
    try {
      const existing = get().bindings.find(
        (b) => b.sceneId === sceneId && b.modelNodeId === modelNodeId && b.visualType === DEVICE_LINK_TYPE,
      );
      if (deviceId) {
        if (existing) {
          const updated = await updateSceneBinding(sceneId, existing.id, {
            deviceId,
            modelNodeName,
            bindPointKey: existing.bindPointKey,
          });
          set((s) => ({
            bindings: s.bindings.map((b) => (b.id === updated.id ? updated : b)),
          }));
        } else {
          const created = await createSceneBinding(sceneId, {
            modelNodeId,
            modelNodeName,
            deviceId,
            visualType: DEVICE_LINK_TYPE,
            ruleConfig: {},
          });
          set((s) => ({ bindings: [...s.bindings, created] }));
        }
      } else if (existing) {
        await removeSceneBinding(sceneId, existing.id);
        set((s) => ({ bindings: s.bindings.filter((b) => b.id !== existing.id) }));
      }
      return true;
    } catch {
      return false;
    }
  },

  setVisualBinding: async (sceneId, modelNodeId, modelNodeName, visualType, data) => {
    try {
      const existing = get().bindings.find(
        (b) => b.sceneId === sceneId && b.modelNodeId === modelNodeId && b.visualType === visualType,
      );
      const hasRule = Boolean(
        data.deviceId && String(data.deviceId).trim() && data.bindPointKey && String(data.bindPointKey).trim(),
      );
      if (hasRule) {
        if (existing) {
          const updated = await updateSceneBinding(sceneId, existing.id, {
            deviceId: data.deviceId,
            bindPointKey: data.bindPointKey,
            ruleConfig: data.ruleConfig ?? existing.ruleConfig,
            modelNodeName,
          });
          set((s) => ({ bindings: s.bindings.map((b) => (b.id === updated.id ? updated : b)) }));
        } else {
          const created = await createSceneBinding(sceneId, {
            modelNodeId,
            modelNodeName,
            deviceId: data.deviceId,
            bindPointKey: data.bindPointKey,
            visualType,
            ruleConfig: data.ruleConfig ?? {},
          });
          set((s) => ({ bindings: [...s.bindings, created] }));
        }
      } else if (existing) {
        await removeSceneBinding(sceneId, existing.id);
        set((s) => ({ bindings: s.bindings.filter((b) => b.id !== existing.id) }));
      }
      return true;
    } catch {
      return false;
    }
  },

  updateBindingRule: async (sceneId, bindingId, patch) => {
    try {
      const existing = get().bindings.find((b) => b.id === bindingId && b.sceneId === sceneId);
      if (!existing) return false;
      const updated = await updateSceneBinding(sceneId, bindingId, {
        ruleConfig: { ...(existing.ruleConfig || {}), ...patch },
      });
      set((s) => ({ bindings: s.bindings.map((b) => (b.id === updated.id ? updated : b)) }));
      return true;
    } catch {
      return false;
    }
  },

  flushDeviceLinks: async (sceneId, models) => {
    try {
      // 阶段7 修复：保留既有非 device-link 绑定（color/height/rotationSpeed/action 视觉规则）——
      // 全量替换若只写 device-link 会删光视觉规则（"保存场景后绑定消失"根因）
      const kept = get().bindings.filter((b) => b.visualType !== DEVICE_LINK_TYPE);
      const links = models
        .filter((m) => m.deviceId && String(m.deviceId).trim())
        .map((m) => ({
          modelNodeId: m.id,
          modelNodeName: m.name,
          deviceId: String(m.deviceId).trim(),
          visualType: DEVICE_LINK_TYPE,
          ruleConfig: {},
        }));
      const saved = await replaceSceneBindings(sceneId, [...kept, ...links]);
      set({ bindings: saved });
      return true;
    } catch {
      return false;
    }
  },

  clear: () => set({ bindings: [], loading: false }),
}));